/**
 * ONE rendered email body, wherever it is read — the message page, the mail modal and (from the
 * timeline-inline build) an expanded row on an entity profile
 * (`docs/specs/strategic-architecture/timeline-inline-message-spec.md` §6).
 *
 * PRESENTATIONAL ONLY, and that is the governance point rather than a style preference: each
 * surface keeps its OWN fetch through its OWN governed, audited door (the F.5 rule), and shares
 * only the rendering. Nothing here fetches, and nothing here decides who may read a body.
 *
 * The five states are the modal body card's, verbatim, because the awkward ones are the reason this
 * is shared at all: a 403 ("not on this thread") and a 404 ("nothing stored") are exactly what a
 * second, hastily-written renderer drops. A surface whose door answers differently overrides the
 * words through `copy` — the record panel's evidence door 404s for every refusal, so it will say
 * "No message body is available" rather than borrowing the email door's distinct wording.
 *
 * `data-inline-body` is the tested contract (CSS-module class names are hashed), the same
 * discipline `EvidenceBody` uses.
 */
import type { CSSProperties, ReactNode } from 'react';

import { ThreadedBody } from './ThreadedBody';
import styles from './InlineMessageBody.module.css';

export type InlineMessageState =
  | 'loading' | 'forbidden' | 'missing' | 'error' | 'ready'
  /** EC-5: an acquisition for THIS body is in flight (spec §3.2 "Retrieving"). */
  | 'retrieving'
  /** EC-5: a completed bounded search found the body in no eligible copy. */
  | 'source_gone';

/** The modal/page wording, and the default for every surface that does not override it. */
export const INLINE_MESSAGE_COPY: Record<Exclude<InlineMessageState, 'ready'>, string> = {
  loading: 'Checking entitlement…',
  // ⛔ "Retrieving" and "missing" are different facts and stay different words: one says we are
  // looking in Microsoft 365 right now, the other says nothing is stored and nobody is looking.
  retrieving: 'Retrieving from Microsoft 365…',
  source_gone: 'No longer available from Microsoft 365.',
  forbidden:
    "You're not on this thread — the body isn't available to you. The metadata above is "
    + 'firm-wide; bodies open only for participants.',
  missing: 'No stored body for this message (metadata only).',
  error: 'Failed to load the body.',
};

export interface InlineMessageBodyProps {
  state: InlineMessageState;
  /** The body text, once it has arrived. Rendered by the F.4 `ThreadedBody` when state is 'ready'. */
  text?: string | null;
  /**
   * Height cap for the box, with the scroll INSIDE it. `'none'` disables both — which is what the
   * message page and the modal pass, because they are already inside their own scroll container and
   * a second, nested one is worse than a long page.
   */
  maxHeight?: string;
  /** Top-right slot, outside the scrolled region — the timeline row's "Full view ↗". */
  header?: ReactNode;
  /** Per-surface wording override, per state. Omitted states keep the default copy. */
  copy?: Partial<Record<Exclude<InlineMessageState, 'ready'>, string>>;
  className?: string;
}

export function InlineMessageBody({
  state, text, maxHeight = '75vh', header, copy, className,
}: InlineMessageBodyProps) {
  const capped = maxHeight !== 'none';
  // The cap travels twice on purpose: as the box's own `max-height`, and as a custom property the
  // stylesheet hands to a sandboxed iframe, which cannot size itself to its content.
  const capStyle = capped
    ? ({ maxHeight, '--inline-body-cap': maxHeight } as CSSProperties)
    : undefined;
  const words = (s: Exclude<InlineMessageState, 'ready'>) => copy?.[s] ?? INLINE_MESSAGE_COPY[s];

  return (
    <div className={className ? `${styles.wrap} ${className}` : styles.wrap}>
      {header != null && <div className={styles.head}>{header}</div>}
      <div
        className={capped ? `${styles.capped}` : undefined}
        style={capStyle}
        data-inline-body={state}
      >
        {state === 'loading' && (
          <div className={styles.loading} role="status">{words('loading')}</div>
        )}
        {state === 'retrieving' && (
          <div className={styles.loading} role="status">{words('retrieving')}</div>
        )}
        {state === 'source_gone' && (
          <div className={styles.missing}>{words('source_gone')}</div>
        )}
        {state === 'forbidden' && <div className={styles.forbidden}>{words('forbidden')}</div>}
        {state === 'missing' && <div className={styles.missing}>{words('missing')}</div>}
        {state === 'error' && (
          <div className={styles.error} role="alert">{words('error')}</div>
        )}
        {state === 'ready' && text != null && <ThreadedBody text={text} />}
      </div>
    </div>
  );
}

export default InlineMessageBody;
